/**
 * What Chromium browsers say about themselves, in the part that needs no
 * permission and no wait. Declared here rather than taken from the DOM types,
 * which do not all carry it, so a test can hand in any browser it likes.
 */
interface UserAgentData {
  brands: readonly { brand: string; version: string }[];
  platform: string;
}

/** The two things a browser offers about itself: `navigator` fits it. */
export interface BrowserSource {
  userAgent: string;
  userAgentData?: UserAgentData;
}

/** A best guess, shown in the form for the attorney to correct. */
export interface Environment {
  browser: string;
  os: string;
}

/**
 * The made-up brand a browser adds to its list so that no site can match the
 * list whole. The specification varies its punctuation, never its words.
 */
const GREASE = /not.a.brand/i;

/** The engine, which a browser built on it lists beside its own name. */
const ENGINE = "Chromium";

function fromBrands(data: UserAgentData | undefined): string {
  const named = (data?.brands ?? []).filter(({ brand }) => !GREASE.test(brand));
  const chosen =
    named.find(({ brand }) => brand !== ENGINE) ??
    named.find(({ brand }) => brand === ENGINE);
  return chosen ? `${chosen.brand} ${chosen.version}` : "";
}

/** A pattern in a user agent string, and what to call a string it matches. */
type Reading = [RegExp, (match: RegExpExecArray) => string];

/**
 * For the browsers with no structured answer: Firefox and Safari, and a
 * Chromium browser on a page that is not served securely. Read in this order
 * because each string also names the browsers it borrows from: Edge's says
 * Chrome and Safari, Chrome's says Safari.
 */
const BROWSERS: readonly Reading[] = [
  [/\bEdg(?:A|iOS)?\/(\d+)/, ([, major]) => `Edge ${major}`],
  [/\bOPR\/(\d+)/, ([, major]) => `Opera ${major}`],
  [/\b(?:Firefox|FxiOS)\/(\d+)/, ([, major]) => `Firefox ${major}`],
  [/\b(?:Chrome|CriOS)\/(\d+)/, ([, major]) => `Chrome ${major}`],
  [/\bVersion\/(\d+).*\bSafari\//, ([, major]) => `Safari ${major}`],
];

/**
 * In the same order-matters shape: an iPhone's string says it is like a Mac,
 * and an Android phone's says Linux. Windows and macOS give no version worth
 * reading, since both freeze the one they report.
 */
const SYSTEMS: readonly Reading[] = [
  [/\biPhone OS (\d+)_(\d+)/, ([, major, minor]) => `iOS ${major}.${minor}`],
  [
    /\biPad; CPU OS (\d+)_(\d+)/,
    ([, major, minor]) => `iPadOS ${major}.${minor}`,
  ],
  [/\bAndroid (\d+(?:\.\d+)?)/, ([, version]) => `Android ${version}`],
  [/\bCrOS\b/, () => "ChromeOS"],
  [/\bWindows NT\b/, () => "Windows"],
  [/\bMac OS X\b/, () => "macOS"],
  [/\bLinux\b/, () => "Linux"],
];

/** The first reading whose pattern the string matches, or blank. */
function read(userAgent: string, table: readonly Reading[]): string {
  for (const [pattern, describe] of table) {
    const match = pattern.exec(userAgent);
    if (match) return describe(match);
  }
  return "";
}

/**
 * The browser and operating system the attorney is using, from what the
 * browser says about itself where it says it, and from its user agent string
 * where it does not. Either can come back blank, and the form asks for it then.
 */
export function detectEnvironment(
  source: BrowserSource = navigator,
): Environment {
  const browser =
    fromBrands(source.userAgentData) || read(source.userAgent, BROWSERS);
  const os = source.userAgentData?.platform || read(source.userAgent, SYSTEMS);
  return { browser, os };
}
